<template>
  <WebLayout
    title="Sobre RealGestión"
    description="Conoce más sobre RealGestión, nuestra misión y el equipo que hace posible la gestión eficiente de tu negocio."
    url="https://realgestion.lat/about"
  >
    <Head :title="`Sobre RealGestión - Conoce más sobre nosotros`" />

    <!-- Hero Section -->
    <div class="bg-white">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            Impulsando el futuro de los negocios en Venezuela
          </h1>
          <p class="mt-6 max-w-2xl mx-auto text-xl text-gray-500">
            En Venezuela existen más de 2 millones de microempresas, de las cuales la gran mayoría aún no ha adoptado tecnología digital. ¡Estamos hablando de miles de negocios y emprendedores que necesitan herramientas para crecer!
          </p>
          <p class="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Es por ello que en RealGestión buscamos brindarles las herramientas tecnológicas necesarias para ayudarlos a controlar sus finanzas, tomar mejores decisiones y hacer crecer su negocio en un entorno cada vez más digital.
          </p>
          <p class="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Creemos en el potencial de los emprendedores venezolanos. Estamos aquí para ser sus aliados en este camino de crecimiento.
          </p>
        </div>
      </div>
    </div>

    <!-- Why RealGestión Section -->
    <div class="bg-gray-50 py-16 sm:py-24">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            ¿Por qué RealGestión?
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            Democratizando la digitalización financiera de los pequeños comerciantes y emprendedores venezolanos.
          </p>
        </div>

        <div class="mt-16">
          <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div class="bg-white rounded-lg shadow-lg p-6">
              <h3 class="text-xl font-bold text-gray-900 mb-4">Inclusión</h3>
              <p class="text-gray-500">
                Buscamos impulsar a los pequeños negocios venezolanos por medio de la inclusión digital y financiera.
              </p>
            </div>
            <div class="bg-white rounded-lg shadow-lg p-6">
              <h3 class="text-xl font-bold text-gray-900 mb-4">Innovación</h3>
              <p class="text-gray-500">
                Soñamos con ser la plataforma líder en Venezuela para microempresarios y emprendedores.
              </p>
            </div>
            <div class="bg-white rounded-lg shadow-lg p-6">
              <h3 class="text-xl font-bold text-gray-900 mb-4">Impacto</h3>
              <p class="text-gray-500">
                Nuestro objetivo es transformar la forma en que los pequeños negocios operan en Venezuela.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Section -->
    <div class="bg-white py-16 sm:py-24">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            El equipo que hace todo posible
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            RealGestión nació de la iniciativa de un equipo apasionado por la tecnología y el emprendimiento en Venezuela.
          </p>
          <p class="mt-4 text-xl text-gray-500">
            Somos un equipo comprometido con el desarrollo de nuestro país, trabajando para apoyar a los negocios locales e impulsar la economía venezolana.
          </p>
        </div>

        <div class="mt-16">
          <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <!-- Team Member 1 -->
            <div class="bg-white rounded-lg shadow-lg overflow-hidden">
              <div class="p-6">
                <h3 class="text-xl font-bold text-gray-900">David González</h3>
                <p class="text-teal-600">Fundador & CEO</p>
                <p class="mt-4 text-gray-500">
                  Apasionado por la tecnología y el emprendimiento, con más de 10 años de experiencia en desarrollo de software y gestión de proyectos.
                </p>
              </div>
            </div>

            <!-- Team Member 2 -->
            <div class="bg-white rounded-lg shadow-lg overflow-hidden">
              <div class="p-6">
                <h3 class="text-xl font-bold text-gray-900">María Rodríguez</h3>
                <p class="text-teal-600">CTO</p>
                <p class="mt-4 text-gray-500">
                  Experta en arquitectura de software y liderazgo técnico, comprometida con la innovación y la excelencia en el desarrollo.
                </p>
              </div>
            </div>

            <!-- Team Member 3 -->
            <div class="bg-white rounded-lg shadow-lg overflow-hidden">
              <div class="p-6">
                <h3 class="text-xl font-bold text-gray-900">Carlos Pérez</h3>
                <p class="text-teal-600">Director de Producto</p>
                <p class="mt-4 text-gray-500">
                  Especialista en experiencia de usuario y diseño de productos digitales, enfocado en crear soluciones intuitivas y efectivas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Values Section -->
    <div class="bg-gray-50 py-16 sm:py-24">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Nuestro ADN
          </h2>
        </div>

        <div class="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          <div class="bg-white rounded-lg shadow-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">Pasión</h3>
            <p class="text-gray-500">
              Por apoyar a los pequeños negocios venezolanos.
            </p>
          </div>

          <div class="bg-white rounded-lg shadow-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">Primero el usuario</h3>
            <p class="text-gray-500">
              Lo que necesita el usuario es nuestra prioridad en la toma de decisiones.
            </p>
          </div>

          <div class="bg-white rounded-lg shadow-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">Piensa en grande</h3>
            <p class="text-gray-500">
              Queremos ser el aliado que lleve la digitalización a todos los micronegocios de Venezuela.
            </p>
          </div>

          <div class="bg-white rounded-lg shadow-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">Estructura Plana</h3>
            <p class="text-gray-500">
              Las buenas ideas pueden venir de cualquiera, desde los fundadores hasta un practicante.
            </p>
          </div>

          <div class="bg-white rounded-lg shadow-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">Meritocracia</h3>
            <p class="text-gray-500">
              El desempeño excepcional siempre tendrá su recompensa.
            </p>
          </div>

          <div class="bg-white rounded-lg shadow-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">Pertenencia</h3>
            <p class="text-gray-500">
              Todos y cada uno debemos pensar y actuar como dueños del producto.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="bg-teal-700">
      <div class="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
        <h2 class="text-3xl font-extrabold text-white sm:text-4xl">
          <span class="block">¿Quieres ser parte de nuestro equipo?</span>
        </h2>
        <p class="mt-4 text-lg leading-6 text-teal-200">
          Únete a nosotros en nuestra misión de transformar la forma en que los pequeños negocios operan en Venezuela.
        </p>
        <a href="mailto:careers@realgestion.lat" class="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-teal-600 bg-white hover:bg-teal-50 sm:w-auto">
          Contáctanos
        </a>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'

export default {
  components: {
    Link,
    Head,
    WebLayout
  }
}
</script> 